import React, { useState, useEffect, useMemo } from 'react';
const getJSONOrValidVal = (...vals) => {
  for (const val of vals) {
    if (val && typeof val === 'object') {
      return val;
    }
    if (typeof val === 'string' && val.trim().startsWith('{')) {
      return val;
    }
  }
  for (const val of vals) {
    if (val !== undefined && val !== null && val !== '' && val !== 0 && val !== '0' && val !== '0.00') {
      return val;
    }
  }
  return null;
};
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchData, clearData } from '@/store/slices/fetch-slice';
import { apiKeys, apiMethods, apiUrls } from '@/store/api/constants';
import { getStatus } from '@/store/slices/helpers/helpers';
import indianNumberFormatComponent from '@/common/helpers/indianNumberFormatComponent';
import { cusRejectStatusColor } from '../constants/constants';

export default function RejectedCustomerEditModal({ open, onClose, customerData, onSuccess }) {
  const dispatch = useDispatch();
  const [rsPerKg, setRsPerKg] = useState('');
  const [selectedMaidaCategory, setSelectedMaidaCategory] = useState('PA');

  const submitStatus = useSelector(state => getStatus(state, apiKeys.resubmitRejectedCustomer, ''));

  useEffect(() => {
    if (customerData) {
      //console.log(customerData, 'customerData');

      const initialVal = getJSONOrValidVal(
        customerData.newSchemeRsPerTon,
        customerData.newSchemeRsPerKg,
        customerData.schemeRsPerTon,
        customerData.schemeRsPerKg,
        customerData.rsPerTon,
        customerData.rsPerKg
      ) ?? 0;
      // If the value is an object (common for Maida categorical rates), stringify it
      if (initialVal && typeof initialVal === 'object') {
        setRsPerKg(JSON.stringify(initialVal));
      } else {
        setRsPerKg(String(initialVal));
      }
    }
  }, [customerData]);

  useEffect(() => {
    if (submitStatus === 'succeeded') {
      dispatch(clearData({ key: apiKeys.resubmitRejectedCustomer }));
      if (onSuccess) onSuccess();
      onClose();
    }
  }, [submitStatus, dispatch, onSuccess, onClose]);

  const formatCategoricalValue = (val, compareVal = null) => {
    if (!val) return '';
    let obj = val;
    let compObj = compareVal || {};

    if (typeof val === 'string' && val.trim().startsWith('{')) {
      try {
        obj = JSON.parse(val);
      } catch (e) {
        return val;
      }
    }
    if (compareVal && typeof compareVal === 'string' && compareVal.trim().startsWith('{')) {
      try {
        compObj = JSON.parse(compareVal);
      } catch (e) {
        compObj = {};
      }
    }

    if (typeof obj === 'object' && obj !== null) {
      const entries = Object.entries(obj).filter(([_, v]) => v !== '' && v !== null && v !== 0 && v !== '0');
      return entries.map(([k, v], i) => {
        const isDiff = compareVal && String(compObj[k]) !== String(v);
        return (
          <React.Fragment key={k}>
            <span style={{
              color: isDiff ? 'red' : 'inherit',
              fontWeight: isDiff ? 'bold' : 'normal',
              backgroundColor: isDiff ? '#ffebee' : 'transparent',
              padding: isDiff ? '2px 4px' : '0',
              borderRadius: '4px'
            }}>
              {k}: {v}
            </span>
            {i < entries.length - 1 ? ', ' : ''}
          </React.Fragment>
        );
      });
    }

    if (compareVal !== null) {
      const isDiff = String(val) !== String(compareVal);
      return <span style={{ color: isDiff ? 'red' : 'inherit', fontWeight: isDiff ? 'bold' : 'normal' }}>{val}</span>;
    }

    return val;
  };

  const calculateNewAmount = useMemo(() => {
    if (!customerData || !rsPerKg) return 0;

    const schemeName = (customerData.schemeNameParent || customerData.schemeName || '').toLowerCase();
    const isMaida = schemeName.includes('maida');
    const isSooji = schemeName.includes('sooji');
    const isBranQap = schemeName.includes('bran qap');

    // Multiplier (x1000) is used for KG-based products (rate is Rs/kg, sales in MT)
    const isKgBasedOriginal = !isMaida && !isSooji && !isBranQap &&
      Number(customerData.schemeRsPerKg || customerData.rsPerKg || 0) > 0;

    const useMultiplier = isMaida || isSooji || isBranQap || isKgBasedOriginal;

    // 1. Parse Sales Data — handle object, JSON string, or plain number
    const salesData = customerData.monthSales;
    let totalSales = 0;
    let salesMap = {};

    if (salesData && typeof salesData === 'object') {
      // API returned an object directly
      salesMap = salesData;
      totalSales = Object.values(salesMap).reduce((a, b) => a + Number(b), 0);
    } else if (typeof salesData === 'string' && salesData.trim().startsWith('{')) {
      try {
        salesMap = JSON.parse(salesData);
        totalSales = Object.values(salesMap).reduce((a, b) => a + Number(b), 0);
      } catch (e) { }
    } else {
      totalSales = Number(salesData || 0);
    }

    // 2. Parse Rate Data — handle object, JSON string, or plain number
    let rates = null;
    if (rsPerKg && typeof rsPerKg === 'object') {
      rates = rsPerKg;
    } else if (typeof rsPerKg === 'string' && rsPerKg.trim().startsWith('{')) {
      try {
        rates = JSON.parse(rsPerKg);
      } catch (e) { }
    }

    // 3. Categorical calculation (Maida per-category rates)
    if (rates) {
      const hasCategorySales = Object.keys(salesMap).length > 0;

      if (hasCategorySales) {
        // Accurate: iterate sales categories, match rates by key (case-insensitive & trimmed)
        let total = 0;
        const ratesUpper = Object.fromEntries(
          Object.entries(rates).map(([k, v]) => [k.trim().toUpperCase(), v])
        );
        Object.entries(salesMap).forEach(([k, salesVal]) => {
          const trimmedKey = k.trim().toUpperCase();
          const r = Number(ratesUpper[trimmedKey] ?? 0);
          const s = Number(salesVal || 0);
          total += s * r * (useMultiplier ? 1000 : 1);
        });
        return total;
      } else {
        // Fallback when no per-category sales: sum all rate*totalSales (approximate)
        let total = 0;
        Object.values(rates).forEach(r => {
          total += totalSales * Number(r || 0) * (useMultiplier ? 1000 : 1);
        });
        return total;
      }
    }

    // 4. Standard single-rate calculation
    const rateVal = Number(rsPerKg || 0);
    return useMultiplier ? (totalSales * rateVal * 1000) : (totalSales * rateVal);
  }, [customerData, rsPerKg]);

  const handleSubmit = () => {
    if (!customerData?.id) return;

    const payload = {
      id: customerData.id,
      new_scheme_rs_per_kg: rsPerKg,
    };

    const targetStage = getTargetStage();
    dispatch(fetchData({
      url: apiUrls.resubmitRejectedCustomer,
      method: apiMethods.POST,
      key: apiKeys.resubmitRejectedCustomer,
      data: payload,
      successMsg: `Customer successfully resubmitted to ${targetStage}.`,
      failureMsg: "Failed to resubmit customer."
    }));
  };

  const handleCategoricalRateChange = (category, newValue) => {
    let currentRates = {};
    // 1. Get current rates from state
    if (typeof rsPerKg === 'string' && rsPerKg.trim().startsWith('{')) {
      try {
        currentRates = JSON.parse(rsPerKg);
      } catch (e) { }
    } else if (typeof rsPerKg === 'object' && rsPerKg !== null) {
      currentRates = rsPerKg;
    } else {
      const flatVal = String(rsPerKg || '');
      if (flatVal !== '' && flatVal !== '0' && flatVal !== '0.00') {
        ['PA', 'PB', 'BA', 'BB', 'MUL'].forEach(cat => {
          currentRates[cat] = flatVal;
        });
      }
    }

    // 2. Get original rates from customer data to ensure all categories are present
    let originalRates = {};
    const orig = getJSONOrValidVal(
      customerData.schemeRsPerTon,
      customerData.schemeRsPerKg,
      customerData.rsPerTon,
      customerData.rsPerKg
    ) || '{}';

    if (typeof orig === 'string' && orig.trim().startsWith('{')) {
      try {
        originalRates = JSON.parse(orig);
      } catch (e) { }
    } else if (typeof orig === 'object' && orig !== null) {
      originalRates = orig;
    } else {
      const flatVal = String(orig || '');
      if (flatVal !== '' && flatVal !== '0' && flatVal !== '0.00' && flatVal !== '{}') {
        ['PA', 'PB', 'BA', 'BB', 'MUL'].forEach(cat => {
          originalRates[cat] = flatVal;
        });
      }
    }

    // 3. Merge: Original + Current + New Category value
    const updatedRates = {
      ...originalRates,
      ...currentRates,
      [category]: newValue
    };

    setRsPerKg(JSON.stringify(updatedRates));
  };

  const isCategoryChanged = (category) => {
    let currentRates = {};
    let originalRates = {};

    try {
      if (typeof rsPerKg === 'string' && rsPerKg.trim().startsWith('{')) {
        currentRates = JSON.parse(rsPerKg);
      } else {
        const flatVal = String(rsPerKg || '');
        if (flatVal !== '' && flatVal !== '0' && flatVal !== '0.00') {
          ['PA', 'PB', 'BA', 'BB', 'MUL'].forEach(cat => {
            currentRates[cat] = flatVal;
          });
        }
      }

      const orig = getJSONOrValidVal(
        customerData.schemeRsPerTon,
        customerData.schemeRsPerKg,
        customerData.rsPerTon,
        customerData.rsPerKg
      ) || '{}';

      if (typeof orig === 'string' && orig.trim().startsWith('{')) {
        originalRates = JSON.parse(orig);
      } else if (typeof orig === 'object' && orig !== null) {
        originalRates = orig;
      } else {
        const flatVal = String(orig || '');
        if (flatVal !== '' && flatVal !== '0' && flatVal !== '0.00' && flatVal !== '{}') {
          ['PA', 'PB', 'BA', 'BB', 'MUL'].forEach(cat => {
            originalRates[cat] = flatVal;
          });
        }
      }
    } catch (e) { }

    return String(currentRates[category] || '') !== String(originalRates[category] || '');
  };

  const getTargetStage = () => {
    if (customerData?.schemeStatus === 7) return "CH Approval";
    if (customerData?.schemeStatus === 9) return "BH Approval";
    if (customerData?.schemeStatus === 11) return "AH Approval";
    return "GM Approval";
  };

  const targetStage = getTargetStage();

  const isSubmitting = submitStatus === 'loading';
  const schemeName = (customerData?.schemeNameParent || customerData?.schemeName || customerData?.productName || '').toLowerCase();
  const isMaida = schemeName.includes('maida');
  const isFeed = schemeName.includes('feed');
  return (
    <Dialog open={open} onClose={!isSubmitting ? onClose : null} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f5f5f5' }}>
        <Typography variant="h6" fontWeight="bold">Edit Rejected Customer Details</Typography>
        <IconButton onClick={onClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, p: 3 }}>
        {customerData && (
          <TableContainer component={Paper} elevation={3}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#057b19ff' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>CUSTOMER CODE</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>CUSTOMER NAME</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>TARGET MTS</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>SALES</TableCell>
                  {isFeed && (
                    <>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>ACHIEVED SLAB (FROM - TO)</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>NEW SLAB (FROM - TO)</TableCell>
                    </>
                  )}
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>RS-PER-TON</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>EDIT RS-PER-TON</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>STATUS</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'right' }}>SCHEME AMOUNT</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'right' }}>UPDATED SCHEME AMOUNT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'center' }}>1</TableCell>
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'center' }}>{customerData.customerCode}</TableCell>
                  <TableCell sx={{ border: '1px solid #eee' }}>{customerData.customerName}</TableCell>
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'center', color: 'blue', fontWeight: 'bold' }}>
                    {(() => {
                      const val = customerData.targetMts;
                      if (typeof val === 'string' && val.trim().startsWith('{')) {
                        try {
                          const obj = JSON.parse(val);
                          const entries = Object.entries(obj).filter(([_, v]) => v !== '' && v !== null && v !== 0 && v !== '0');
                          return entries.map(([k, v], i) => (
                            <span key={k}>
                              {k}: {v}{i < entries.length - 1 ? ', ' : ''}
                            </span>
                          ));
                        } catch (e) {
                          return val;
                        }
                      }
                      return val ? Math.round(val) : (customerData.minMts && customerData.maxMts ? `${customerData.minMts}-${customerData.maxMts}` : 0);
                    })()}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'center', color: 'green', fontWeight: 'bold' }}>
                    {(() => {
                      const val = customerData.monthSales;
                      if (typeof val === 'string' && val.startsWith('{')) {
                        try {
                          const obj = JSON.parse(val);
                          return Object.values(obj).reduce((a, b) => a + Number(b), 0).toFixed(2);
                        } catch (e) { return val; }
                      }
                      return Number(val || 0).toFixed(2);
                    })()}
                  </TableCell>
                  {isFeed && (
                    <>
                      <TableCell sx={{ border: '1px solid #eee', textAlign: 'center' }}>
                        {customerData.achievedSlabFrom} - {customerData.achievedSlabTo}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #eee', textAlign: 'center' }}>
                        {customerData.newAchievedSlabFrom} - {customerData.newAchievedSlabTo}
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'center' }}>
                    {formatCategoricalValue(
                      getJSONOrValidVal(
                        customerData.schemeRsPerTon,
                        customerData.schemeRsPerKg,
                        customerData.rsPerTon,
                        customerData.rsPerKg
                      )
                    ) || `${Number(customerData.minRsPerKg).toFixed(1)}-${Number(customerData.maxRsPerKg).toFixed(1)}`}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'center', minWidth: '220px' }}>
                    {isMaida ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={selectedMaidaCategory}
                            onChange={(e) => setSelectedMaidaCategory(e.target.value)}
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              backgroundColor: isCategoryChanged(selectedMaidaCategory) ? '#fff3e0' : 'inherit'
                            }}
                            MenuProps={{ disablePortal: true }}
                          >
                            {['PA', 'PB', 'BA', 'BB', 'MUL'].map(cat => (
                              <MenuItem
                                key={cat}
                                value={cat}
                                sx={{
                                  backgroundColor: isCategoryChanged(cat) ? '#fff3e0' : 'inherit',
                                  '&.Mui-selected': { backgroundColor: isCategoryChanged(cat) ? '#ffe0b2' : 'inherit' }
                                }}
                              >
                                {cat} {isCategoryChanged(cat) ? '*' : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Rate"
                          value={(() => {
                            if (typeof rsPerKg === 'string' && rsPerKg.trim().startsWith('{')) {
                              try {
                                const obj = JSON.parse(rsPerKg);
                                return obj[selectedMaidaCategory] || '';
                              } catch (e) { return ''; }
                            }
                            return '';
                          })()}
                          onChange={(e) => handleCategoricalRateChange(selectedMaidaCategory, e.target.value)}
                          disabled={isSubmitting}
                          sx={{
                            '& .MuiInputBase-root': {
                              fontSize: '0.875rem',
                              color: 'purple',
                              fontWeight: 'bold',
                              backgroundColor: isCategoryChanged(selectedMaidaCategory) ? '#fff3e0' : 'inherit'
                            }
                          }}
                        />
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        value={rsPerKg}
                        onChange={(e) => setRsPerKg(e.target.value)}
                        disabled={isSubmitting}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: '0.875rem',
                            color: 'purple',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'center', fontWeight: 'bold' }}>
                    {(() => {
                      const { text, backgroundColor, textColor } = cusRejectStatusColor(customerData.schemeStatus);
                      return (
                        <Chip
                          label={text}
                          size="small"
                          sx={{
                            backgroundColor,
                            color: textColor,
                            fontSize: '11px',
                            height: '24px'
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'right', fontWeight: 'bold' }}>
                    {indianNumberFormatComponent(customerData.eligibleSchemeAmount || customerData.schemeAmount)}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid #eee', textAlign: 'right', fontWeight: 'bold', color: 'green' }}>
                    {indianNumberFormatComponent(calculateNewAmount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            * To edit values for Maida/Feed categories, enter a JSON string like: <b>{"{\"PA\":\"2.5\",\"PB\":\"1.5\"}"}</b>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f9f9f9', borderTop: '1px solid #eee' }}>
        <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !rsPerKg}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ backgroundColor: '#057b19ff', '&:hover': { backgroundColor: '#046a15' } }}
        >
          {isSubmitting ? 'Resubmitting...' : `Save & Resubmit to ${targetStage}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
